/**
 * @fileoverview Sales service for fetching sales with filters, pagination, and sorting.
 * @module features/orders/api/salesService
 */

import apiClient from '@/lib/apiClient'
import { salesResponseSchema, type SalesResponse } from '@/features/orders/schemas/salesSchema'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'

/**
 * Query parameters for fetching sales.
 */
export interface SalesParams {
  page?: number
  size?: number
  sort?: string
  direction?: 'asc' | 'desc'
  startDate?: Date | string | null
  endDate?: Date | string | null
  skus?: string[]
  countries?: string[]
}

/**
 * Normalize dates using toDateOnlyStringLocal to match existing API usage across the app.
 */
function normalizeDateParam(value: Date | string | null | undefined): string | undefined {
  if (!value) return undefined
  if (value instanceof Date) {
    return toDateOnlyStringLocal(value)
  }
  // assume already YYYY-MM-DD
  return value
}

/**
 * Build query params object compatible with backend API.
 * - Dates are serialized as YYYY-MM-DD (local) using toDateOnlyStringLocal
 * - Arrays (skus, countries) are converted to comma-separated strings
 */
function buildQueryParams(params: SalesParams): Record<string, unknown> {
  const query: Record<string, unknown> = {}

  if (typeof params.page === 'number') query.page = params.page
  if (typeof params.size === 'number') query.size = params.size
  if (params.sort) query.sort = params.sort
  if (params.direction) query.direction = params.direction

  const start = normalizeDateParam(params.startDate)
  const end = normalizeDateParam(params.endDate)
  if (start) query.startDate = start
  if (end) query.endDate = end

  if (params.skus && params.skus.length > 0) {
    query.skus = params.skus.join(',') as unknown as string
  }
  if (params.countries && params.countries.length > 0) {
    query.countries = params.countries.join(',') as unknown as string
  }

  return query
}

function unwrapSalesPayload(payload: unknown): unknown {
  let current: unknown = payload
  for (let i = 0; i < 2; i += 1) {
    if (current && typeof current === 'object' && 'data' in (current as Record<string, unknown>)) {
      const nested = (current as { data?: unknown }).data
      if (nested !== undefined) {
        current = nested
        continue
      }
    }
    break
  }
  return current
}

function normalizeSalesPayload(payload: unknown): SalesResponse {
  const unwrapped = unwrapSalesPayload(payload)

  // If backend ever returns a bare array, lift it into a page-like shape
  if (Array.isArray(unwrapped)) {
    return {
      content: unwrapped as unknown[],
      totalElements: unwrapped.length,
      totalPages: 1,
      size: unwrapped.length,
      number: 0,
    } as SalesResponse
  }

  const parsed = salesResponseSchema.safeParse(unwrapped)
  if (!parsed.success) {
    throw new Error('API validation failed for SalesResponse')
  }
  return parsed.data
}

/**
 * Sales service API.
 */
export const salesService = {
  /**
   * Fetch paginated sales using provided filters.
   *
   * @param params SalesParams
   * @returns Parsed and validated SalesResponse
   * @throws Error when network fails or validation fails
   * @example
   * ```ts
   * const res = await salesService.getSales({ page: 0, size: 25, sort: "date", direction: "desc" });
   * ```
   */
  async getSales(params: SalesParams = {}): Promise<SalesResponse> {
    const queryParams = buildQueryParams(params)
    const response = await apiClient.get('/sales', { params: queryParams })
    return normalizeSalesPayload(response.data)
  },
}
